export const FULLSCREEN_URLS = [
  '/login',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/redirect',
];

/**
 * Routes that use a full-bleed layout: no padding, no footer, sidebar auto-collapses
 * and overlays on open. Used for pages like the map that need maximum real-estate.
 */
export const FULLBLEED_URLS = ['/map'];

/**
 * Routes that lock the shell to the viewport height (desktop/tablet only) so the
 * page itself never scrolls — internal panels (tables, filters) scroll instead.
 * Footer and shell padding are kept, unlike {@link FULLBLEED_URLS}.
 */
/** `/projects/:id` also covers `/projects/:id/discover` via trailing-path match. */
export const FILL_HEIGHT_URLS = ['/projects/:id'];

/**
 * Routes where the global sidebar collapses automatically on desktop so that
 * the page has more horizontal room. The user can still re-open it manually.
 */
export const COLLAPSED_SIDEBAR_URLS = [
  '/dashboard',
  '/docs',
  '/factory/:id/edit',
  '/factory/:id', // View factory
  '/projects/:id/discover',
  '/projects/:id',
  '/reporting',
];

/** URL prefixes that may use the unauthenticated public docs shell layout. */
export const PUBLIC_DOCS_URL_PREFIXES = ['/docs'];

/**
 * Returns whether the URL is under the public documentation route tree.
 *
 * Pair with {@link APP_SESSION.isAuthenticated} in layout code to activate
 * the public docs shell (minimal topbar, no sidebar).
 *
 * @example
 * isPublicDocsRoute('/docs/terms-of-service'); // true
 */
export function isPublicDocsRoute(url: string): boolean {
  return PUBLIC_DOCS_URL_PREFIXES.some((prefix) => url.startsWith(prefix));
}

/** Converts a route pattern like `/factory/:id/edit` into a regex that matches real URLs. */
function routePatternToRegex(pattern: string): RegExp {
  const escaped = pattern.replace(/:[^/]+/g, '[^/]+');
  return new RegExp(`^${escaped}(/|$)`);
}

/** Returns whether `pattern` matches `url` (supports `:param` segments). */
function matchesRoutePattern(pattern: string, url: string): boolean {
  return pattern.includes(':')
    ? routePatternToRegex(pattern).test(url)
    : url.startsWith(pattern);
}

/**
 * Full-bleed routes and {@link COLLAPSED_SIDEBAR_URLS}: desktop sidebar auto-collapses.
 */
export function isCollapsedSidebarRoute(url: string): boolean {
  return (
    FULLBLEED_URLS.some((pattern) => matchesRoutePattern(pattern, url)) ||
    COLLAPSED_SIDEBAR_URLS.some((pattern) => matchesRoutePattern(pattern, url))
  );
}

/**
 * Returns whether the URL uses the fill-height shell mode
 * ({@link FILL_HEIGHT_URLS}): viewport-locked main area with internal scrolling.
 *
 * @example
 * isFillHeightRoute('/projects/42'); // true
 * isFillHeightRoute('/projects/42/discover'); // true
 */
export function isFillHeightRoute(url: string): boolean {
  return FILL_HEIGHT_URLS.some((pattern) => matchesRoutePattern(pattern, url));
}
